import { useCallback, useEffect, useRef } from 'react'

import { ResponsiveLine } from '@nivo/line'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_RETENTION_CURVE } from 'src/graphql/retentionMetrics.graphql'

import AnalyticsCard from '../../components/AnalyticsCard'

import type { StatusLogFiltersInput } from 'types/graphql'

const INFO_TEXT = [
  'Of every 100 hires you made during this period, the line shows how many were still active at each day after hire — through day 120.',
  'A steep drop in the first 30–60 days usually points to hiring-fit issues. A slower decline later points to longer-term retention factors like pay, schedule, or working conditions.',
  'Each point only counts hires whose full window has already passed, so points further to the right are based on a smaller but more complete sample.',
].join('\n\n')

const CHART_HEIGHT = 280
const LINE_COLOR = '#2563eb'
const AREA_COLOR = 'rgba(37, 99, 235, 0.08)'
const MARKER_COLOR = '#9ca3af'
const X_MAX = 120
const MARKER_DAYS = [30, 60, 90] as const

export interface RetentionCurvePoint {
  day: number
  retainedPct: number
  activeCount: number
}

export interface RetentionCurveCardViewProps {
  cohortSize: number
  points: ReadonlyArray<RetentionCurvePoint>
  loading?: boolean
}

/**
 * Pure view layer for the retention-curve card. Wrap in {@link RetentionCurveCard}
 * to fetch its own data; passing data directly is useful for Storybook and tests.
 */
export const RetentionCurveCardView = ({
  cohortSize,
  points,
  loading = false,
}: RetentionCurveCardViewProps) => {
  if (loading) {
    return (
      <AnalyticsCard title="Retention Curve" info={INFO_TEXT} loading>
        <div />
      </AnalyticsCard>
    )
  }

  return (
    <AnalyticsCard title="Retention Curve" info={INFO_TEXT}>
      <p
        className="text-xs text-doubleNickel-gray-500"
        data-testid="retention-curve-subtitle"
      >
        Cohort size:{' '}
        <span
          className="font-mono font-semibold tabular-nums text-doubleNickel-gray-700"
          data-testid="retention-curve-cohort-size"
        >
          {cohortSize.toLocaleString()}
        </span>{' '}
        hires. Day 30 / 60 / 90 marked with dashed lines.
      </p>

      {points.length === 0 ? (
        <div className="flex h-[200px] items-center justify-center text-xs text-doubleNickel-gray-500">
          No retention data for this period.
        </div>
      ) : (
        <div style={{ height: CHART_HEIGHT }} data-testid="retention-curve-chart">
          <ResponsiveLine
            data={[
              {
                id: '% retained',
                data: points.map((p) => ({ x: p.day, y: p.retainedPct })),
              },
            ]}
            margin={{ top: 36, right: 24, bottom: 44, left: 48 }}
            xScale={{ type: 'linear', min: 0, max: X_MAX }}
            yScale={{ type: 'linear', min: 0, max: 100 }}
            curve="monotoneX"
            colors={[LINE_COLOR]}
            lineWidth={2}
            enableArea
            areaOpacity={1}
            defs={[
              {
                id: 'retentionFill',
                type: 'linearGradient',
                colors: [
                  { offset: 0, color: AREA_COLOR },
                  { offset: 100, color: AREA_COLOR },
                ],
              },
            ]}
            fill={[{ match: '*', id: 'retentionFill' }]}
            enablePoints
            pointSize={6}
            pointColor={LINE_COLOR}
            pointBorderWidth={1.5}
            pointBorderColor="#FFFFFF"
            // Voronoi mesh so hover snaps to the nearest data point instead of
            // requiring a pixel-perfect hover on the point itself. Without this,
            // sparse point grids (we sample ~11 days across 120) make the
            // tooltip unreachable.
            useMesh
            enableGridX={false}
            enableGridY
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              tickValues: [0, 15, 30, 45, 60, 75, 90, 105, 120],
              legend: 'Days since hire',
              legendOffset: 36,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              tickValues: 5,
              format: (v: number) => `${v}%`,
            }}
            theme={{
              axis: {
                ticks: { text: { fill: '#9CA3AF', fontSize: 11 } },
                legend: { text: { fill: '#6B7280', fontSize: 11 } },
              },
              grid: { line: { stroke: '#E5E7EB', strokeDasharray: '2 3' } },
            }}
            markers={MARKER_DAYS.map((day) => ({
              axis: 'x' as const,
              value: day,
              lineStyle: {
                stroke: MARKER_COLOR,
                strokeWidth: 1,
                strokeDasharray: '4 4',
              },
              legend: `Day ${day}`,
              // Horizontal label hugging the top of the marker line — fits in
              // the 36px top margin without clipping. The previous "vertical
              // + top" combo rotated the text 90° and overran the margin.
              legendOrientation: 'horizontal' as const,
              legendPosition: 'top' as const,
              legendOffsetY: -8,
              textStyle: {
                fill: '#6B7280',
                fontSize: 11,
                fontWeight: 500,
              },
            }))}
            tooltip={({ point }) => {
              const day = Number(point.data.x)
              const pct = Number(point.data.y)
              // Look up the original point so we can surface the absolute
              // active-count alongside the percentage — keeps the chart's
              // data shape (x/y only) but lets the tooltip stay information-
              // dense without making each plot point heavier.
              const original = points.find((p) => p.day === day)
              const activeCount = original?.activeCount
              return (
                <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-[11px] shadow-md">
                  <div className="text-gray-500">Day {day}</div>
                  <div className="font-mono font-semibold tabular-nums text-gray-900">
                    {pct.toFixed(1)}% retained
                  </div>
                  {activeCount != null && (
                    <div
                      className="font-mono tabular-nums text-gray-500"
                      data-testid="retention-curve-tooltip-counts"
                    >
                      {activeCount.toLocaleString()} of{' '}
                      {cohortSize.toLocaleString()} hires
                    </div>
                  )}
                </div>
              )
            }}
          />
        </div>
      )}
    </AnalyticsCard>
  )
}

export interface RetentionCurveCardProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button — when this value changes (after
   * the initial mount), the card calls its own `refetch()` to re-pull data
   * with the same filters.
   */
  refreshKey: number
}

const RetentionCurveCard = ({
  filters,
  refreshKey,
}: RetentionCurveCardProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_RETENTION_CURVE, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  // Skip the initial mount — useQuery already kicked off the first fetch.
  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const cohortSize = data?.retentionCurve?.cohortSize ?? 0
  const points = (data?.retentionCurve?.points ??
    []) as ReadonlyArray<RetentionCurvePoint>

  return (
    <RetentionCurveCardView
      cohortSize={cohortSize}
      points={points}
      loading={loading && !data}
    />
  )
}

export default RetentionCurveCard
